'use client';
import { useEffect } from 'react';

/** Two short dual-tone bursts every three seconds, synthesized so no audio asset ships. */
const PERIOD_S = 3;
const BURSTS_S = [0, 0.6];
const BURST_LENGTH_S = 0.4;
const FADE_S = 0.02;
const FREQUENCIES_HZ = [440, 480];
const LEVEL = 0.12;

function ringBuffer(context: AudioContext): AudioBuffer {
  const rate = context.sampleRate;
  const buffer = context.createBuffer(1, Math.round(rate * PERIOD_S), rate);
  const samples = buffer.getChannelData(0);
  for (const start of BURSTS_S) {
    const offset = Math.round(start * rate);
    for (let i = 0; i < Math.round(BURST_LENGTH_S * rate); i++) {
      const time = i / rate;
      const envelope = Math.min(1, time / FADE_S, (BURST_LENGTH_S - time) / FADE_S);
      samples[offset + i] = LEVEL * envelope * FREQUENCIES_HZ.reduce((sum, frequency) => sum + Math.sin(2 * Math.PI * frequency * time), 0);
    }
  }
  return buffer;
}

/** Loops the ringtone while `ringing`. A page the user never interacted with keeps the context suspended,
 * so it stays silent and the system notification is the only sound. */
export function useRingtone(ringing: boolean): void {
  useEffect(() => {
    if (!ringing) return;
    const context = new AudioContext();
    const source = context.createBufferSource();
    source.buffer = ringBuffer(context); source.loop = true;
    source.connect(context.destination); source.start();
    // Closing the context rejects a resume still waiting for user activation; that ring has already ended.
    context.resume().catch(() => undefined);
    return () => { source.stop(); void context.close(); };
  }, [ringing]);
}
